import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import styled from 'styled-components';

import themes, { THEME_TYPES } from '@Styles/Themes';

import { useProjectByProjectId, setProjects } from '@Services/Projects';
import { createProject, editProject, getProjects } from '@Services/Projects/Request';
import Layout from '@Templates/Layout';
import TX from '@Components/Common/Text';
import Button from '@Components/Common/Button';

import { ProjectInput, ProjectImage } from '@Components/ProjectSetting';
import ProjectTag from '@Components/ProjectSetting/ProjectTag';
import { useSub } from '@Services/User';
import SnackBar from '@Components/Common/SnackBar';
import { projectImageList } from './ProjectImageList';

const PROJECT_IMAGE_DEFAULT_ID = '0';

const PROJECT_MAX_LENGTH = {
  TITLE: 20,
  DESCRIPTION: 288,
};

const ProjectSetting = () => {
  const navigate = useNavigate();
  const snackBarRef = useRef(null);

  const [searchParams] = useSearchParams();
  const projectIdByParams = searchParams.get('id');
  const isEditMode = !!projectIdByParams;
  const targetProject = useSelector(useProjectByProjectId(projectIdByParams), shallowEqual);
  const theme = themes[THEME_TYPES.LIGHT];

  const dispatch = useDispatch();

  const sub = useSelector(useSub);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mainImageId, setMainImageId] = useState(PROJECT_IMAGE_DEFAULT_ID);
  const [isThrottled, setIsThrottled] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tags, setTags] = useState([]);
  const isProjectSaveButtonDisabled = !name.length || !description.length;

  const moveToProfile = () => navigate('/', { replace: true });

  const setProjectDataByProjectId = () => {
    if (targetProject) {
      setName(targetProject.name);
      setDescription(targetProject.description);
      setMainImageId(targetProject.mainImageId);
      setTags(targetProject.tags);
      setSelectedTags(targetProject.tags);
    }
  };

  const setProjectData = useCallback(async () => {
    const projects = await getProjects(sub);
    dispatch(setProjects({ projects }));
  }, [dispatch, sub]);

  const handleProjectSaveClick = async () => {
    if (!isThrottled) {
      setIsThrottled(true);
      try {
        const project = {
          name,
          description,
          tags,
          mainImageId,
        };
        if (isEditMode) {
          project.id = projectIdByParams;
          await editProject(project);
          await setProjectData();
          navigate(-1);
        } else {
          await createProject(project);
          moveToProfile();
        }
      } catch (error) {
        // TODO: 에러 S3 에 보내기
        console.log(error);
      } finally {
        setTimeout(() => {
          setIsThrottled(false);
        }, 10000);
      }
    }
  };

  // const handleProjectSaveClick = async () => {
  //   try {
  //     const project = {
  //       name,
  //       description,
  //       tags,
  //       mainImageId,
  //     };
  //     if (isEditMode) {
  //       project.id = projectIdByParams;
  //       await editProject(project);
  //       navigate(-1);
  //     } else {
  //       await createProject(project);
  //       moveToProfile();
  //     }
  //   } catch (error) {
  //     // TODO: 에러 S3 에 보내기
  //     console.log(error);
  //   }
  // };

  useEffect(() => {
    setProjectDataByProjectId();
  }, []);

  const handleBackFunc = () => {
    if (isEditMode) {
      navigate(-1);
    } else {
      moveToProfile();
    }
  };

  return (
    <Layout>
      <Container>
        <ProjectInput
          input={name}
          setInput={setName}
          maxLength={PROJECT_MAX_LENGTH.TITLE}
          placeholder="프로젝트 제목"
        />

        <ProjectInput
          input={description}
          setInput={setDescription}
          maxLength={PROJECT_MAX_LENGTH.DESCRIPTION}
          placeholder="프로젝트 설명"
          isAbleMultiline
        />

        <ProjectTag tags={tags} setTags={setTags} selectedTags={selectedTags} setSelectedTag={setSelectedTags} />

        <ImageContainer>
          <TitleText>메인 이미지</TitleText>

          <ScrollContainer>
            {projectImageList.map(({ id, imageURL }) => (
              <ProjectImage
                key={id}
                image={imageURL}
                onClick={() => setMainImageId(id)}
                isChecked={mainImageId === id}
              />
            ))}
          </ScrollContainer>
        </ImageContainer>

        <ButtonContainer>
          <Button
            text={isEditMode ? '닫기' : '취소'}
            textColor={theme.colors.ui_on_1}
            backgroundColor={theme.colors.ui_2}
            onClick={handleBackFunc}
          />

          <Button
            text={isEditMode ? '저장' : '프로젝트 만들기'}
            disabled={isProjectSaveButtonDisabled}
            onClick={handleProjectSaveClick}
          />
        </ButtonContainer>
      </Container>
      <SnackBar ref={snackBarRef} />
    </Layout>
  );
};

const Container = styled.div`
  height: 100vh;
  padding-top: 20vh;
`;

const ImageContainer = styled.div`
  height: 204px;
  background-color: ${({ theme }) => theme.colors.ui_2};
  padding: 16px 0px 25px 20px;
  margin: 40px 0 40px 0;
  border-radius: 12px;
`;

const TitleText = styled(TX.BodyM)`
  line-height: 24px;
  color: ${({ theme }) => theme.colors.ui_on_3};
`;

const ScrollContainer = styled.div`
  margin-top: 18px;
  white-space: nowrap;
  overflow-y: visible;
  overflow-x: scroll;
  height: 140px;

  &::-webkit-scrollbar-thumb {
    margin-right: 20px;
  }
  ::-webkit-scrollbar-track {
    border: solid 3px transparent;
    margin-right: 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;

  padding-left: 20px;
  gap: 0px 10px;
`;

export default React.memo(ProjectSetting);

// import React, { useState, useEffect } from 'react';
// import { useNavigate, useSearchParams } from 'react-router-dom';
// import { useSelector, shallowEqual } from 'react-redux';
// import styled from 'styled-components';

// import themes, { THEME_TYPES } from '@Styles/Themes';

// import { useProjectByProjectId } from '@Services/Projects';
// import { createProject, editProject } from '@Services/Projects/Request';

// import Layout from '@Templates/Layout';
// import TX from '@Components/Common/Text';
// import Button from '@Components/Common/Button';

// import { ProjectInput, ProjectImage } from '@Components/ProjectSetting';
// import ProjectTag from '@Components/ProjectSetting/ProjectTag';
// import { projectImageList } from './ProjectImageList';

// const PROJECT_IMAGE_DEFAULT_ID = '0';

// const PROJECT_MAX_LENGTH = {
//   TITLE: 20,
//   DESCRIPTION: 288,
// };

// const ProjectSetting = () => {
//   const navigate = useNavigate();
//   const [searchParams] = useSearchParams();
//   const projectIdByParams = searchParams.get('id');
//   const isEditMode = !!projectIdByParams;
//   const targetProject = useSelector(useProjectByProjectId(projectIdByParams), shallowEqual);
//   const theme = themes[THEME_TYPES.LIGHT];

//   const [name, setName] = useState('');
//   const [description, setDescription] = useState('');
//   const [mainImageId, setMainImageId] = useState(PROJECT_IMAGE_DEFAULT_ID);
//   const [isThrottled, setIsThrottled] = useState(false);
//   const [selectedTags, setSelectedTags] = useState([]);
//   const [tags, setTags] = useState([]);
//   const isProjectSaveButtonDisabled = !name.length || !description.length;

//   const moveToProfile = () => navigate('/', { replace: true });

//   const setProjectDataByProjectId = () => {
//     if (targetProject) {
//       setName(targetProject.name);
//       setDescription(targetProject.description);
//       setMainImageId(targetProject.mainImageId);
//       setTags(targetProject.tags);
//       setSelectedTags(targetProject.tags);
//     }
//   };

//   const handleProjectSaveClick = async () => {
//     if (!isThrottled) {
//       setIsThrottled(true);
//       try {
//         const project = {
//           name,
//           description,
//           tags,
//           mainImageId,
//         };
//         if (isEditMode) {
//           project.id = projectIdByParams;
//           await editProject(project);
//           navigate(-1);
//         } else {
//           await createProject(project);
//           moveToProfile();
//         }
//       } catch (error) {
//         // TODO: 에러 S3 에 보내기
//         console.log(error);
//       } finally {
//         setTimeout(() => {
//           setIsThrottled(false);
//         }, 10000);
//       }
//     }
//   };

//   // const handleProjectSaveClick = async () => {
//   //   try {
//   //     const project = {
//   //       name,
//   //       description,
//   //       tags,
//   //       mainImageId,
//   //     };
//   //     if (isEditMode) {
//   //       project.id = projectIdByParams;
//   //       await editProject(project);
//   //       navigate(-1);
//   //     } else {
//   //       await createProject(project);
//   //       moveToProfile();
//   //     }
//   //   } catch (error) {
//   //     // TODO: 에러 S3 에 보내기
//   //     console.log(error);
//   //   }
//   // };

//   useEffect(() => {
//     setProjectDataByProjectId();
//   }, []);

//   const handleBackFunc = () => {
//     if (isEditMode) {
//       navigate(-1);
//     } else {
//       moveToProfile();
//     }
//   };
//   return (
//     <Layout>
//       <Container>
//         <ProjectInput
//           input={name}
//           setInput={setName}
//           maxLength={PROJECT_MAX_LENGTH.TITLE}
//           placeholder="프로젝트 제목"
//         />

//         <ProjectInput
//           input={description}
//           setInput={setDescription}
//           maxLength={PROJECT_MAX_LENGTH.DESCRIPTION}
//           placeholder="프로젝트 설명"
//           isAbleMultiline
//         />

//         <ProjectTag tags={tags} setTags={setTags} selectedTags={selectedTags} setSelectedTag={setSelectedTags} />

//         <ImageContainer>
//           <TitleText>메인 이미지</TitleText>

//           <ScrollContainer>
//             {projectImageList.map(({ id, imageURL }) => (
//               <ProjectImage
//                 key={id}
//                 image={imageURL}
//                 onClick={() => setMainImageId(id)}
//                 isChecked={mainImageId === id}
//               />
//             ))}
//           </ScrollContainer>
//         </ImageContainer>

//         <ButtonContainer>
//           <Button
//             text={isEditMode ? '닫기' : '취소'}
//             textColor={theme.colors.ui_on_1}
//             backgroundColor={theme.colors.ui_2}
//             onClick={handleBackFunc}
//           />

//           <Button
//             text={isEditMode ? '저장' : '프로젝트 만들기'}
//             disabled={isProjectSaveButtonDisabled}
//             onClick={handleProjectSaveClick}
//           />
//         </ButtonContainer>
//       </Container>
//     </Layout>
//   );
// };

// const Container = styled.div`
//   height: 100vh;
//   padding-top: 20vh;
// `;

// const ImageContainer = styled.div`
//   height: 204px;
//   background-color: ${({ theme }) => theme.colors.ui_2};
//   padding: 17px 0 25px 20px;
//   margin: 40px 0 40px 0;
//   border-radius: 12px;
// `;

// const TitleText = styled(TX.BodyM)`
//   line-height: 24px;
//   color: ${({ theme }) => theme.colors.ui_on_3};
// `;

// const ScrollContainer = styled.div`
//   margin-top: 18px;
//   white-space: nowrap;
//   overflow: scroll;

//   /* &::-webkit-scrollbar {
//     -webkit-appearance: none;
//     width: 0;
//     height: 0;
//     display: none !important;
//   } */
// `;

// const ButtonContainer = styled.div`
//   display: flex;
//   flex-direction: row;

//   padding-left: 20px;
//   gap: 0px 10px;
// `;

// export default React.memo(ProjectSetting);
